import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import type {
  BodyScrollEndEvent,
  CellEditingStartedEvent,
  CellEditingStoppedEvent,
  CellValueChangedEvent,
  ColDef,
  FilterChangedEvent,
  ColumnState,
  GetMainMenuItemsParams,
  GridReadyEvent,
  RowClickedEvent,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { GridRowActions, isGridInteractiveTarget, type GridRowAction } from '../GridRowActions/GridRowActions';
import { useCanExportGridData } from '../../utils/gridExportCapability';
import { ExportButton, type ExportConfiguration } from '../DataExport/ExportButton';
import { describeExportFilters, type ExportRow } from '../../utils/dataExport';
import { clientGridOrder, gridExportColumns } from '../../utils/gridExportColumns';
import { useGlobalMetadata } from '../../utils/providers';
import { resetColumnLayout } from '../../utils/resetColumnLayout';
import { filterColumnState, useGridLayout } from '../../hooks/useGridLayout';
import { saveGridColumnDefault, useGridColumnDefault } from '../../hooks/useGridColumnDefaults';
import { isUserColumnEvent } from '../../utils/columnState';
import {
  applyGridColumnDefaults,
  defaultEntriesFromGrid,
  reconcileSavedLayout,
  visibleColumnIds,
  type GridDefaultsSnapshot,
} from '../../utils/gridColumnDefaults';
import { ResetColumnsButton } from '../ui/ResetColumnsButton';
import { SaveDefaultLayoutButton } from '../ui/SaveDefaultLayoutButton';
import { ResetFiltersButton } from '../ui/ResetFiltersButton';
import { ActiveFilterChips } from '../ui/ActiveFilterChips';
import { useActiveColumnFilters } from '../ui/useActiveColumnFilters';
import { withClientSideCheckboxFilters } from '../TableGrid/gridCheckboxFilters';
import { resetGridFilters } from '../../utils/resetGridFilters';
import { useHoverPrefetch } from '../../hooks/useHoverPrefetch';
import { useGridPopupCleanup } from '../../hooks/useGridPopupCleanup';
import { columnMenuWithStat, type ColumnStatSource } from '../ui/ColumnStatMenuItem';
import { aggregateValuesForPlan, formatColumnStat, type ColumnStatPlan } from '../../utils/columnStats';
import './WorkspaceGrid.css';

export interface WorkspaceGridProps<T> {
  /** Row data (already loaded client-side). */
  rows: T[];
  /** Column definitions. Numeric columns should use `type: 'rightAligned'`. */
  columns: ColDef<T>[];
  /** Stable row id getter (used by AG Grid for transactions / selection). */
  getRowId: (row: T) => string;
  /** Open / select handler fired on row click. */
  onRowClick?: (row: T) => void;
  /** Read-only speculative work for a row that is likely to be opened. */
  onRowIntent?: (row: T) => void | Promise<void>;
  /** Compact row-level actions. The grid stays entity-agnostic. */
  rowActions?: GridRowAction<T>[];
  /** Show the loading overlay. */
  loading?: boolean;
  /** Overlay text when there are no rows. */
  emptyMessage?: string;
  /**
   * Save the user's column layout (width, order, visibility, sort, pin) under
   * this key, per user on the server (REG-828). A layout this key used to keep
   * in localStorage moves to the server the first time it loads. The key also
   * names the grid's admin default columns (core_crm.grid_column_default),
   * which manage_default_layouts holders save from the grid and edit under Admin > Grid Columns.
   */
  storageKey?: string;
  /** Fixed height (px or CSS). When omitted the grid flex-fills its parent. */
  height?: number | string;
  /** Row height — defaults to 40 to fit status pills / avatars. */
  rowHeight?: number;
  /** Header height; defaults to 34. Zoomed tables scale it with the rows. */
  headerHeight?: number;
  /** Extra class on the grid container (for per-table tweaks). */
  className?: string;
  /** Called when the user scrolls near the bottom — used for incremental loading. */
  onReachBottom?: () => void;
  /** Enables AG Grid row selection. */
  rowSelection?: 'single' | 'multiple';
  /** Called with the selected row data whenever the selection changes. */
  onSelectionChanged?: (rows: T[]) => void;
  /** Increment this to clear the grid's selection from the parent. */
  selectionResetKey?: number;
  /** URL- or parent-controlled sort state. */
  sortModel?: Array<{ colId: string; sort: 'asc' | 'desc'; sortIndex?: number }>;
  /** Called after a user changes the sort. */
  onSortModelChange?: (sortModel: Array<{ colId: string; sort: 'asc' | 'desc'; sortIndex?: number }>) => void;
  /**
   * Called with the ids of every displayed column after the column chooser
   * changes what is shown. Grids whose rows are fetched with an explicit column
   * list use this to notice that a newly revealed column was never fetched.
   */
  onDisplayedColumnsChange?: (colIds: string[]) => void;
  /** REG-802: stat line at the bottom of each column's three-dot menu. */
  columnStats?: WorkspaceGridColumnStats;
  /**
   * Inline editing (REG-739, see useTableInlineEdit): while on, a single
   * click opens the column's editor instead of opening the row, and the cell
   * events below carry the write / lock handling.
   */
  editMode?: boolean;
  onCellValueChanged?: (event: CellValueChangedEvent<T>) => void;
  onCellEditingStarted?: (event: CellEditingStartedEvent<T>) => void;
  onCellEditingStopped?: (event: CellEditingStoppedEvent<T>) => void;
  /**
   * REG-814: an expandable detail panel under a row (the Opportunities tabs'
   * transcript, thread and history). `expandedRowId` is the one open row (by
   * getRowId), or null; the panel renders full width under it.
   */
  detailRenderer?: (row: T) => React.ReactNode;
  expandedRowId?: string | null;
  /** Classes on the outer shell (per-page density / zebra rules). */
  shellClassName?: string;
  /** Render every column, not only those in view (narrow grids). */
  suppressColumnVirtualisation?: boolean;
  /**
   * REG-874: show Export (for users with export access) under this name. The
   * file has the grid's columns, order and display text.
   */
  exportName?: string;
  /**
   * Every row the export should include when `rows` is only the loaded part
   * (paged tabs). Without it the export takes the rows on hand, after the
   * grid's own filters and sort.
   */
  exportRows?: (signal: AbortSignal) => Promise<{ rows: T[]; total?: number }>;
  /** Overrides the grid export capability check (see ExportButton `allowed`). */
  exportAllowed?: boolean;
  /**
   * REG-917 (Opportunities): Export opens on the selected rows when there are
   * any, and takes only the selected rows the grid still shows, in its order;
   * a column filter that hides a selected row unselects it. Off: the dialog
   * offers every selected row and opens on all records, as before.
   */
  exportSelectionAsShown?: boolean;
}

/**
 * REG-802: how a WorkspaceGrid column is summarised. With no column filter the
 * page's own query can aggregate the whole set server-side (`server`). Column
 * filters here run in the browser, so with one active the stat is taken over
 * the filtered rows on hand, which is the whole set once every page is loaded.
 */
export interface WorkspaceGridColumnStats {
  plan: (colId: string) => ColumnStatPlan | null;
  server?: (colId: string, plan: ColumnStatPlan) => ColumnStatSource | null;
  /** True when `rows` already holds every row the page's filters match. */
  allRowsLoaded: boolean;
}

/**
 * What a saved layout keeps per column: visibility, width, pinning and (unless
 * the page owns the sort) the sort. Array order is the column order.
 */
function compactColumnState(state: ColumnState[], withoutSort: boolean): ColumnState[] {
  return state.map((column) => {
    const kept: ColumnState = { colId: column.colId };
    if (column.hide != null) kept.hide = column.hide;
    if (column.width != null) kept.width = column.width;
    if (column.pinned != null) kept.pinned = column.pinned;
    if (!withoutSort && column.sort != null) {
      kept.sort = column.sort;
      kept.sortIndex = column.sortIndex;
    }
    return kept;
  });
}

function displayedSelectedRows<T>(api: GridReadyEvent<T>['api']): T[] {
  const picked: T[] = [];
  api.forEachNodeAfterFilterAndSort((node) => { if (node.data && !node.group && node.isSelected()) picked.push(node.data); });
  return picked;
}

const DEFAULT_COL_DEF: ColDef = {
  sortable: true,
  filter: true,
  resizable: true,
  minWidth: 70,
};

export default function WorkspaceGrid<T>({
  rows,
  columns,
  getRowId,
  onRowClick,
  onRowIntent,
  rowActions = [],
  loading = false,
  emptyMessage = 'No rows.',
  storageKey,
  height,
  rowHeight = 40,
  headerHeight = 34,
  className = '',
  onReachBottom,
  rowSelection,
  onSelectionChanged,
  selectionResetKey = 0,
  onDisplayedColumnsChange,
  sortModel,
  onSortModelChange,
  columnStats,
  editMode = false,
  onCellValueChanged,
  onCellEditingStarted,
  onCellEditingStopped,
  detailRenderer,
  expandedRowId = null,
  shellClassName = '',
  suppressColumnVirtualisation = false,
  exportName,
  exportRows,
  exportAllowed,
  exportSelectionAsShown = false,
}: WorkspaceGridProps<T>) {
  const canExportGridData = useCanExportGridData();
  const { data: metadata } = useGlobalMetadata();
  const apiRef = useRef<GridReadyEvent<T>['api'] | null>(null);
  // Close the column chooser / menus with the grid (they live on document.body).
  const onGridPreDestroyed = useGridPopupCleanup(() => apiRef.current);
  const applyingControlledSort = useRef(false);
  const [gridReady, setGridReady] = useState(false);
  const activeFilters = useActiveColumnFilters();
  const {
    loaded: layoutLoaded,
    initialColumnState: savedLayout,
    save: saveLayout,
    clear: clearLayout,
    initialDefaults: savedAgainst,
  } = useGridLayout(storageKey, { legacyStorageKey: storageKey });
  const {
    loaded: defaultsLoaded,
    gridDefault,
    refresh: refreshDefaults,
  } = useGridColumnDefault(storageKey);
  // The key whose saved layout is on the grid; nothing is saved before that,
  // so an early column event cannot overwrite the user's layout with defaults.
  const restoredLayoutKey = useRef<string | null>(null);
  const applyingLayout = useRef(false);
  const { schedule: scheduleRowPrefetch, cancel: cancelRowPrefetch } = useHoverPrefetch();
  // REG-828: the page's columns, then the admin default on top of them.
  const pageColumns = useMemo(() => withClientSideCheckboxFilters(columns), [columns]);
  const defaultedColumns = useMemo(
    () => applyGridColumnDefaults(pageColumns, gridDefault),
    [gridDefault, pageColumns],
  );
  const pageDefaultVisible = useMemo(() => visibleColumnIds(pageColumns), [pageColumns]);
  const currentDefaults = useMemo<GridDefaultsSnapshot>(() => ({
    version: gridDefault?.version ?? 0,
    visible: visibleColumnIds(defaultedColumns),
  }), [defaultedColumns, gridDefault?.version]);
  const effectiveColumns = useMemo<ColDef<T>[]>(() => {
    if (rowActions.length === 0) return defaultedColumns;
    // Labeled pill buttons need more room than the compact icon glyphs.
    const actionWidth = rowActions.some((action) => action.variant === 'button') ? 62 : 46;
    const actionColumn: ColDef<T> = {
      colId: '__row_actions',
      headerName: '',
      width: actionWidth,
      minWidth: actionWidth,
      maxWidth: actionWidth,
      pinned: 'left',
      sortable: false,
      filter: false,
      resizable: false,
      suppressMenu: true,
      cellRenderer: ({ data }: { data?: T }) => (
        data ? <GridRowActions row={data} actions={rowActions} /> : null
      ),
    };
    const selectionIndex = defaultedColumns.findIndex(
      (column) => column.colId === 'bulk-selection' || Boolean(column.checkboxSelection),
    );
    if (selectionIndex < 0) return [actionColumn, ...defaultedColumns];
    return [
      ...defaultedColumns.slice(0, selectionIndex + 1),
      actionColumn,
      ...defaultedColumns.slice(selectionIndex + 1),
    ];
  }, [defaultedColumns, rowActions]);

  // A parent that owns the sort (sortModel, usually from the URL) keeps it out
  // of the saved layout: restoring a saved sort over the URL's would make the
  // two fight. Otherwise the sort is part of the layout, as before.
  const sortControlled = sortModel !== undefined;

  // Only a change the user made is saved (see isUserColumnEvent).
  const persist = useCallback((event: { source?: string }) => {
    if (!storageKey || !apiRef.current || applyingLayout.current || !isUserColumnEvent(event)) return;
    if (restoredLayoutKey.current !== storageKey) return;
    saveLayout(compactColumnState(apiRef.current.getColumnState(), sortControlled), currentDefaults);
  }, [currentDefaults, saveLayout, sortControlled, storageKey]);

  // Restore the saved layout once the grid and the layout are both ready.
  useEffect(() => {
    const api = apiRef.current;
    if (!gridReady || !api || !storageKey || !layoutLoaded || !defaultsLoaded) return;
    if (restoredLayoutKey.current === storageKey) return;
    const saved: ColumnState[] = compactColumnState(reconcileSavedLayout(
      filterColumnState(savedLayout, (api.getColumns() ?? []).map((column) => column.getColId())),
      savedAgainst,
      currentDefaults,
      pageDefaultVisible,
    ), sortControlled);
    applyingLayout.current = true;
    applyingControlledSort.current = true;
    if (saved.length > 0) api.applyColumnState({ state: saved, applyOrder: true });
    if (sortModel) {
      api.applyColumnState({
        defaultState: { sort: null, sortIndex: null },
        state: sortModel.map((item, index) => ({
          colId: item.colId,
          sort: item.sort,
          sortIndex: item.sortIndex ?? index,
        })),
      });
    }
    applyingControlledSort.current = false;
    applyingLayout.current = false;
    restoredLayoutKey.current = storageKey;
    onDisplayedColumnsChange?.(api.getAllDisplayedColumns().map((column) => column.getColId()));
    // sortModel is applied by its own effect after this first restore.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridReady, layoutLoaded, defaultsLoaded, savedLayout, storageKey]);

  const onGridReady = useCallback((event: GridReadyEvent<T>) => {
    apiRef.current = event.api;
    setGridReady(true);
    activeFilters.attach(event.api);
    if (sortModel) {
      applyingControlledSort.current = true;
      event.api.applyColumnState({
        defaultState: { sort: null, sortIndex: null },
        state: sortModel.map((item, index) => ({
          colId: item.colId,
          sort: item.sort,
          sortIndex: item.sortIndex ?? index,
        })),
      });
      applyingControlledSort.current = false;
    }
    // Saved column state can restore columns that were revealed in an earlier
    // session, and applying it here does not always raise a visibility event.
    // Report the initial set so callers that fetch by column list start correct.
    onDisplayedColumnsChange?.(
      event.api.getAllDisplayedColumns().map((column) => column.getColId()),
    );
  }, [activeFilters.attach, onDisplayedColumnsChange, sortModel, storageKey]);

  useEffect(() => {
    if (!apiRef.current || !sortModel) return;
    applyingControlledSort.current = true;
    apiRef.current.applyColumnState({
      defaultState: { sort: null, sortIndex: null },
      state: sortModel.map((item, index) => ({
        colId: item.colId,
        sort: item.sort,
        sortIndex: item.sortIndex ?? index,
      })),
    });
    applyingControlledSort.current = false;
  }, [sortModel]);

  const handleSortChanged = useCallback((event: { source?: string }) => {
    if (!sortControlled) persist(event);
    if (applyingControlledSort.current || !apiRef.current || !onSortModelChange) return;
    const next = apiRef.current.getColumnState()
      .filter((column) => column.sort === 'asc' || column.sort === 'desc')
      .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
      .map((column, index) => ({
        colId: column.colId,
        sort: column.sort as 'asc' | 'desc',
        sortIndex: column.sortIndex ?? index,
      }));
    onSortModelChange(next);
  }, [onSortModelChange, persist, sortControlled]);

  const handleRowClicked = useCallback((event: RowClickedEvent<T>) => {
    // In edit mode a click is for the cell editor, not for opening the row.
    if (editMode || isGridInteractiveTarget(event.event?.target ?? null)) return;
    if (onRowClick && event.data) onRowClick(event.data);
  }, [editMode, onRowClick]);
  const handleRowMouseEnter = useCallback((event: { data?: T }) => {
    if (event.data && onRowIntent) scheduleRowPrefetch(() => onRowIntent(event.data!));
  }, [onRowIntent, scheduleRowPrefetch]);

  const handleSelectionChanged = useCallback((event: SelectionChangedEvent<T>) => {
    onSelectionChanged?.(event.api.getSelectedRows());
  }, [onSelectionChanged]);

  useEffect(() => {
    if (selectionResetKey) apiRef.current?.deselectAll();
  }, [selectionResetKey]);

  // REG-917 opt-in: a column filter that hides a selected row unselects it, so
  // the selection count, the Export dialog and the file always agree.
  const handleFilterChanged = useCallback((event: FilterChangedEvent<T>) => {
    const shown = new Set<string>();
    event.api.forEachNodeAfterFilter((node) => { if (node.id) shown.add(node.id); });
    const hidden = event.api.getSelectedNodes().filter((node) => !node.id || !shown.has(node.id));
    if (hidden.length) event.api.setNodesSelected({ nodes: hidden, newValue: false, source: 'api' });
  }, []);

  // REG-814: one open detail panel, driven by the parent.
  useEffect(() => {
    const api = apiRef.current;
    if (!gridReady || !api || !detailRenderer) return;
    api.forEachNode((node) => {
      if (node.detail) return;
      const open = expandedRowId != null && node.id === expandedRowId;
      if (node.expanded !== open) node.setExpanded(open);
    });
  }, [detailRenderer, expandedRowId, gridReady, rows]);
  const DetailPanel = useMemo(() => {
    if (!detailRenderer) return undefined;
    const Panel = ({ data }: { data?: T }) => (
      data ? <div className="workspace-grid-detail">{detailRenderer(data)}</div> : null
    );
    return Panel;
  }, [detailRenderer]);

  const handleBodyScrollEnd = useCallback((event: BodyScrollEndEvent<T>) => {
    if (!onReachBottom) return;
    const range = event.api.getVerticalPixelRange();
    const total = event.api.getDisplayedRowCount() * rowHeight;
    if (total - range.bottom < 240) onReachBottom();
  }, [onReachBottom, rowHeight]);

  const handleResetColumns = useCallback(() => {
    if (!apiRef.current) return;
    applyingControlledSort.current = true;
    applyingLayout.current = true;
    resetColumnLayout(apiRef.current, storageKey);
    applyingLayout.current = false;
    applyingControlledSort.current = false;
    clearLayout();
    onSortModelChange?.([]);
  }, [clearLayout, onSortModelChange, storageKey]);

  // REG-828: a manage_default_layouts holder makes the columns on screen everyone's default.
  // Their own saved layout is cleared so they follow the default like everyone.
  const handleSaveDefaultLayout = useCallback(async () => {
    if (!apiRef.current || !storageKey) return;
    const entries = defaultEntriesFromGrid(apiRef.current.getColumnState(), pageColumns, gridDefault);
    await saveGridColumnDefault(storageKey, entries);
    clearLayout();
    await refreshDefaults();
  }, [clearLayout, gridDefault, pageColumns, refreshDefaults, storageKey]);

  const handleResetFilters = useCallback(() => {
    if (apiRef.current) resetGridFilters(apiRef.current);
  }, []);

  const getMainMenuItems = useCallback((params: GetMainMenuItemsParams<T>) => {
    if (!columnStats) return params.defaultItems;
    const colId = params.column.getColId();
    const plan = columnStats.plan(colId);
    if (!plan) return params.defaultItems;
    if (!params.api.isColumnFilterPresent() && columnStats.server) {
      return columnMenuWithStat(params, columnStats.server(colId, plan));
    }
    const values: unknown[] = [];
    params.api.forEachNodeAfterFilter((node) => {
      if (node.data) values.push(params.api.getCellValue({ rowNode: node, colKey: colId }));
    });
    // Client values carry the column's display value, not a server label column.
    const clientPlan = { ...plan, column: colId, labelColumn: undefined } as ColumnStatPlan;
    const result = formatColumnStat(clientPlan, aggregateValuesForPlan(clientPlan, values));
    if (!columnStats.allRowsLoaded) {
      result.lines.push(`Loaded rows only (${values.length.toLocaleString('en-US')})`);
    }
    return columnMenuWithStat(params, {
      queryKey: ['workspace-column-stat', storageKey ?? '', colId, result.lines],
      load: async () => result,
    });
  }, [columnStats, storageKey]);

  const getExportConfiguration = (): ExportConfiguration => {
    const api = apiRef.current!;
    const columns = gridExportColumns(api, () => true, metadata?.routeData?.tableToRouteName);
    const labels = Object.fromEntries(columns.map((column) => [column.key, column.label]));
    const filterModel = api.getFilterModel() ?? {};
    // A column filter runs in the browser over the loaded rows only, so with one
    // set the export takes exactly the rows the grid shows.
    const fetchAll = exportRows && Object.keys(filterModel).length === 0;
    let all: ExportRow[] | null = null;
    let notice: string | undefined;
    return {
      name: exportName ?? 'Export',
      columns,
      // Fetched rows come in the server's order; the file follows the grid's sort.
      order: clientGridOrder(api),
      // REG-917 opt-in: the selected rows the grid still shows, in its order. A
      // row a column filter now hides is not exported even if it stays ticked.
      selectedRows: rowSelection
        ? (exportSelectionAsShown ? displayedSelectedRows(api) : api.getSelectedRows()) as ExportRow[]
        : undefined,
      preferSelected: exportSelectionAsShown,
      filterSummary: [describeExportFilters(filterModel, labels), fetchAll || !exportRows ? '' : 'Column filters apply to the loaded rows only.']
        .filter(Boolean).join(' '),
      load: async ({ offset, limit, signal }) => {
        if (!all) {
          if (fetchAll) {
            const fetched = await exportRows(signal);
            all = fetched.rows as ExportRow[];
            if (fetched.total != null && fetched.total > all.length) {
              notice = `The first ${all.length.toLocaleString('en-US')} of ${fetched.total.toLocaleString('en-US')} matching records.`;
            }
          } else {
            const shown: ExportRow[] = [];
            api.forEachNodeAfterFilterAndSort((node) => { if (node.data && !node.group) shown.push(node.data as ExportRow); });
            all = shown;
          }
        }
        return { rows: all.slice(offset, offset + limit), total: all.length, notice };
      },
    };
  };

  const fill = height == null;
  const popupParent = useMemo<HTMLElement | undefined>(
    () => (typeof document === 'undefined' ? undefined : document.body),
    [],
  );

  return (
    <div
      className={`workspace-grid-shell ${fill ? 'workspace-grid-shell--fill' : ''} ${shellClassName}`}
      style={fill ? undefined : { height, width: '100%' }}
    >
      <div className="workspace-grid-controls">
        <ResetColumnsButton disabled={!gridReady} onReset={handleResetColumns} />
        {storageKey ? <SaveDefaultLayoutButton disabled={!gridReady} onSave={handleSaveDefaultLayout} /> : null}
        <ResetFiltersButton disabled={!gridReady} activeCount={activeFilters.filters.length} onReset={handleResetFilters} />
        {exportName ? <ExportButton disabled={!gridReady} allowed={exportAllowed} getConfiguration={getExportConfiguration} /> : null}
      </div>
      <ActiveFilterChips filters={activeFilters.filters} onRemove={activeFilters.removeFilter} />
      <div className={`ag-theme-alpine workspace-grid ${fill ? 'workspace-grid--fill' : ''} ${className}`}>
        {/* The admin default shapes the column definitions, so the grid waits for it (one shared cached read). */}
        {defaultsLoaded ? <AgGridReact<T>
          rowData={rows}
          columnDefs={effectiveColumns}
          defaultColDef={DEFAULT_COL_DEF}
          getRowId={(params) => getRowId(params.data)}
          rowHeight={rowHeight}
          headerHeight={headerHeight}
          // New rowData arrives frequently through realtime query invalidation. Avoid
          // replaying AG Grid's opacity/position animation for every background refresh.
          animateRows={false}
          // Editors need cell focus; outside edit mode the grid is click-to-open.
          suppressCellFocus={!editMode}
          singleClickEdit={editMode}
          stopEditingWhenCellsLoseFocus
          undoRedoCellEditing
          // A dropdown editor opened on one of the last rows would be clipped
          // by the grid viewport; anchoring popups to the document lets it escape.
          popupParent={popupParent}
          onCellValueChanged={onCellValueChanged}
          onCellEditingStarted={onCellEditingStarted}
          onCellEditingStopped={onCellEditingStopped}
          suppressDragLeaveHidesColumns
          suppressCsvExport={!canExportGridData}
          suppressExcelExport={!canExportGridData}
          suppressRowClickSelection={!!rowSelection}
          loading={loading}
          onGridReady={onGridReady}
          onGridPreDestroyed={onGridPreDestroyed}
          onRowClicked={onRowClick ? handleRowClicked : undefined}
          onCellMouseOver={onRowIntent ? handleRowMouseEnter : undefined}
          onCellMouseOut={onRowIntent ? cancelRowPrefetch : undefined}
          rowSelection={rowSelection}
          onSelectionChanged={onSelectionChanged ? handleSelectionChanged : undefined}
          onFilterChanged={exportSelectionAsShown && rowSelection ? handleFilterChanged : undefined}
          onSortChanged={handleSortChanged}
          getMainMenuItems={columnStats ? getMainMenuItems : undefined}
          onColumnMoved={(event) => { if (event.finished) persist(event); }}
          onColumnResized={(event) => { if (event.finished) persist(event); }}
          onColumnPinned={persist}
        onColumnVisible={(event) => {
          persist(event);
          onDisplayedColumnsChange?.(
            event.api.getAllDisplayedColumns().map((column) => column.getColId()),
          );
        }}
          onBodyScrollEnd={onReachBottom ? handleBodyScrollEnd : undefined}
          suppressColumnVirtualisation={suppressColumnVirtualisation}
          masterDetail={Boolean(DetailPanel)}
          isRowMaster={DetailPanel ? () => true : undefined}
          detailCellRenderer={DetailPanel}
          detailRowAutoHeight={Boolean(DetailPanel)}
          overlayNoRowsTemplate={`<span class="workspace-grid-empty">${emptyMessage}</span>`}
        /> : null}
      </div>
    </div>
  );
}
